import _ from 'underscore';
import React, { useContext, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { PerformanceTile } from '@bingads-webui-campaign-react/performance-tile';
import { BidStrategy, CampaignSubTypeMapping } from '@bingads-webui-campaign/audience-campaign-shared';
import { AgeRangesCampaignToAdInsight } from '@bingads-webui-campaign-react/xandr-campaign';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';

import { useTargetingEstimationCaller } from './use-targeting-estimation-caller';
// import { formatBigDecimal } from '../../../shared/helper/format-big-decimal';
import { getValueWithUnit } from '../../../shared/helper/scale-unit';
import { ReachEstimate } from './reach-estimate';
import { BiddingSchemeToBidType, BidTypes } from '../../../common/constants';
import { getAudienceIds, getExcludedAudienceIds, getInterestIds, getExcludedInterestIds, getOtherAudienceIds, getExcludedOtherAudienceIds } from '../utils';

const hasRemartketingList = audience => _.any(audience || [], audienceCriterion =>
  audienceCriterion.Criterion.AudienceType === 'Remessaging'
  || audienceCriterion.Criterion.AudienceType === 'Product'
  || audienceCriterion.Criterion.AudienceType === 'Custom'
  || audienceCriterion.Criterion.AudienceType === 'RemarketingSimilar');

const getTargetsFromAdgroupModel = (adGroup) => {
  const ageRage = _.map(adGroup.ageRanges || [], (age) => {
    if (_.isObject(age) && _.get(age, ['Criterion', 'AgeCriterion', 'Target'])) {
      return AgeRangesCampaignToAdInsight[_.get(age, ['Criterion', 'AgeCriterion', 'Target'])];
    }
    return AgeRangesCampaignToAdInsight[age];
  });

  const genderTypes = _.map(adGroup.genderTypes || [], (gender) => {
    if (_.isObject(gender) && _.get(gender, ['Criterion', 'GenderCriterion', 'Target'])) {
      return _.get(gender, ['Criterion', 'GenderCriterion', 'Target']);
    }
    return gender;
  });

  return {
    Age: {
      Includes: ageRage,
      Excludes: [],
    },
    Gender: {
      Includes: genderTypes,
      Excludes: [],
    },
    Location: {
      Includes: _.map(
        _.filter(_.get(adGroup, 'locationTargets.currentLevelSettings.currentLocations', []), location => location.SelectionStatus === 'Included' && location.CriterionType !== 'Radius'),
        location => (location.Criterion.LocationCriterion.LocationId)
      ),
      Excludes: _.map(
        _.filter(_.get(adGroup, 'locationTargets.currentLevelSettings.currentLocations', []), location => location.SelectionStatus === 'Excluded' && location.CriterionType !== 'Radius'),
        location => (location.Criterion.LocationCriterion.LocationId)
      ),
    },
    RadiusTargets: _.map(
      _.filter(_.get(adGroup, 'locationTargets.currentLevelSettings.currentLocations', []), location => location.CriterionType === 'Radius'),
      location => ({
        CenterLatitude: location.Criterion.RadiusCriterion.Latitude,
        CenterLongitude: location.Criterion.RadiusCriterion.Longitude,
        Radius: location.Criterion.RadiusCriterion.Radius,
      })
    ),
    Audience: {
      Includes: getAudienceIds({ adGroup }),
      Excludes: getExcludedAudienceIds({ adGroup }),
    },
    Company: {
      Includes: _.map(_.get(adGroup, 'companyNameTargets', []), company => company.Target),
      Excludes: _.map(_.get(adGroup, 'negativeCompanyNameTargets', []), company => company.Target),
    },
    Industry: {
      Includes: _.get(adGroup, 'industry', []),
      Excludes: _.get(adGroup, 'negativeIndustry', []),
    },
    JobFunction: {
      Includes: _.get(adGroup, 'jobTargets', []),
      Excludes: _.get(adGroup, 'negativeJobTargets', []),
    },
  };
};

const getOtherParams = (adGroup, campaign) => ({
  AutoTargeting: adGroup.UseOptimizedTargeting,
  CampaignBiddingStrategy: (campaign && BidStrategy[campaign.BiddingScheme.Type]),
  CampaignSubType: adGroup ? CampaignSubTypeMapping[adGroup.CampaignSubType] : campaign.CampaignSubType,
});

export const TargetingEstimation = ({
  data,
  isCampaignCreationPlannerIntegrationEnabled,
  isMultiAdFormatEnabled,
  showBreakdown,
  isAudienceAdGroupEdit,
  audienceUsePrivacyCheck,
}) => {
  const i18n = useContext(I18nContext);
  const activity = useContext(ActivityContext);

  const { adGroup = {}, campaign = {} } = data || {};

  const hasAudienceRemarketingList = hasRemartketingList(adGroup.audiences || []);
  const otherParams = useMemo(() => getOtherParams(adGroup, campaign), [adGroup, campaign]);

  const [targetsForAudienceSizeEstimation, targetsForAudienceKPIEstimation] =
    useMemo(() => {
      const targetsForAudienceSize = { ...getTargetsFromAdgroupModel(adGroup), ...otherParams };

      const bidKeyToSend = BiddingSchemeToBidType[campaign.BiddingScheme.Type] || BidTypes.SearchBid;

      const bidValue = adGroup && adGroup[bidKeyToSend];

      const targetsForAudienceKPI =
      {
        ...getTargetsFromAdgroupModel(adGroup),
        Bid: Number(bidValue),
        DailyBudget: campaign && campaign.Budget && campaign.Budget.Amount,
      };

      return [targetsForAudienceSize, targetsForAudienceKPI];
    }, [adGroup, campaign, otherParams]);

  const interestOnlyTargets = useMemo(() => ({
    Audience: {
      Includes: getInterestIds({ adGroup }),
      Excludes: getExcludedInterestIds({ adGroup }),
    },
    ...otherParams,
  }), [adGroup, otherParams]);

  const otherAudienceOnlyTargets = useMemo(() => ({
    Audience: {
      Includes: getOtherAudienceIds({ adGroup }),
      Excludes: getExcludedOtherAudienceIds({ adGroup }),
    },
    ...otherParams,
  }), [adGroup, otherParams]);

  // AudienceStatic = 0,
  // AudienceVideoAds = 3,
  // DisplayAds = 5,
  const getCampaingSubTypes = types => _.map(types, (type) => {
    if (type === 'Display') return 5;
    if (type === 'Video') return 3;
    return 0;
  });

  const subTypes = _.map(data.adGroup.ads, ad => ad.adSubType);
  const campaignSubTypes = isMultiAdFormatEnabled ?
    getCampaingSubTypes(subTypes)
    : undefined;

  const [
    isRecommendationDataLoading,
    estimationData,
    isImpressionTooSpecific,
    privacyResult,
    audienceSize,
    ageOnlyEstimation,
    genderOnlyEstimation,
    locationOnlyEstimation,
    audienceOnlyEstimation,
    interestOnlyEstimation,
  ] = useTargetingEstimationCaller({
    targetsForAudienceSizeEstimation,
    targetsForAudienceKPIEstimation,
    showBreakdown,
    interestOnlyTargets,
    otherAudienceOnlyTargets,
    campaignSubTypes,
    audienceUsePrivacyCheck,
  });

  // Log estimationData and adGroup.id only once
  useEffect(() => {
    if (activity && estimationData) {
      activity.signal(`PlannerValidation AdGroup ID: ${adGroup.Id} - ${JSON.stringify(estimationData)}`);
    }
  }, [estimationData, adGroup.id]);

  const estimations = useMemo(() => {
    const estClick = (estimationData && estimationData.EstClick && estimationData.EstClick.High &&
      getValueWithUnit(estimationData.EstClick.High.Value, estimationData.EstClick.High.Unit)) || 0;
    const estCTR = (estimationData && estimationData.EstCTR && estimationData.EstCTR.High && estimationData.EstCTR.Low) ?
      [`${estimationData.EstCTR.Low}%`, `${estimationData.EstCTR.High}%`]
      : 0;
    const estSpend = (estimationData && estimationData.EstSpend && estimationData.EstSpend.High &&
      getValueWithUnit(estimationData.EstSpend.High.Value, estimationData.EstSpend.High.Unit)) || 0;
    const estAvgCPC = (estimationData && estimationData.EstAvgCPC && estimationData.EstAvgCPC.High &&
      getValueWithUnit(estimationData.EstAvgCPC.High.Value, estimationData.EstAvgCPC.High.Unit)) || 0;

    return _.compact([
      {
        className: 'audience-clicks',
        title: i18n.getString(_TL_('Clicks')),
        dashColor: '#0078D7',
        estimation: estClick,
      },
      {
        className: 'audience-CTR',
        title: i18n.getString(_TL_('CTR')),
        dashColor: '#00A5B0',
        estimation: estCTR,
      },
      isCampaignCreationPlannerIntegrationEnabled && {
        className: 'audience-Spend',
        title: i18n.getString(_TL_('Spend')),
        dashColor: '#00A5B0',
        estimation: estSpend,
      },
      isCampaignCreationPlannerIntegrationEnabled && {
        className: 'audience-AvgCPC',
        title: i18n.getString(_TL_('Avg.CPC')),
        dashColor: '#00A5B0',
        estimation: estAvgCPC,
      },
    ]);
  }, [estimationData, i18n, isCampaignCreationPlannerIntegrationEnabled]);

  // disable per https://msasg.visualstudio.com/Bing_Ads/_workitems/edit/8822650
  const showImpressions = false;
  const showPerformanceTile = false;

  return (
    <div>
      <ReachEstimate
        i18n={i18n}
        estimationData={estimationData}
        isLoading={isRecommendationDataLoading}
        pricingModel={targetsForAudienceSizeEstimation.CampaignBiddingStrategy}
        isImpressionTooSpecific={isImpressionTooSpecific}
        privacyResult={privacyResult}
        hasAudienceRemarketingList={hasAudienceRemarketingList}
        isCampaignCreationPlannerIntegrationEnabled={isCampaignCreationPlannerIntegrationEnabled}
        showImpressions={showImpressions}
        showBreakdown={showBreakdown}
        audienceSize={audienceSize}
        isAudienceAdGroupEdit={isAudienceAdGroupEdit}
        ageOnlyEstimation={ageOnlyEstimation}
        genderOnlyEstimation={genderOnlyEstimation}
        locationOnlyEstimation={locationOnlyEstimation}
        audienceOnlyEstimation={audienceOnlyEstimation}
        interestOnlyEstimation={interestOnlyEstimation}
      />
      {showPerformanceTile &&
        <PerformanceTile
          className="estimate-reach"
          heading={i18n.getString(_TL_('Estimated monthly performance'))}
          description={i18n.getString(_TL_('These are estimates based on businesses with similar ad settings.'))}
          note={i18n.getString(_TL_('These are estimated figures and do not guarantee future results.'))}
          estimations={estimations}
          showShimmer={isRecommendationDataLoading}
          isDisplayPlusCampaign
        />
      }
    </div>
  );
};

TargetingEstimation.propTypes = {
  data: PropTypes.shape({}),
  isCampaignCreationPlannerIntegrationEnabled: PropTypes.bool,
  showBreakdown: PropTypes.bool,
  isAudienceAdGroupEdit: PropTypes.bool.isRequired,
};

TargetingEstimation.defaultProps = {
  data: {},
  isCampaignCreationPlannerIntegrationEnabled: false,
  showBreakdown: false,
};
